import { prop } from 'js/utils/ramda.util';
import { useCallback, useMemo } from 'react';
import { useFormatter } from 'js/hooks/useFormatter';
import { usePortletConfig, usePortletDispatch, usePortletSelector } from 'js/components/system/Portlet';

import * as actions from './actions';

export const usePortletActions = (initType) => {
    const dispatch = usePortletDispatch();

    return useMemo(() => ({
        init: (preferences) => {
            dispatch(actions.initPreferencesAction(preferences));
        },
        changeOne: ({ checked, type = initType, index }) => {
            dispatch(actions.changePreferencesAction(checked, type, index));
        },
        changeAll: ({ checked, type = initType }) => {
            dispatch(actions.changeAllPreferencesAction(checked, type));
        }
    }), [dispatch, initType]);
};

export const useEventHandlers = (initType) => {
    const { changeOne, changeAll } = usePortletActions(initType);

    return {
        onChange: useCallback((e) => {
            const { target: { checked, dataset: { index } } } = e;
            changeOne({ checked, index: Number(index) });
        }, [changeOne]),
        onChangeAll: useCallback((e) => {
            const { target: { checked } } = e;
            changeAll({ checked });
        }, [changeAll])
    };
};

export const useListProps = (type) => {
    const items = usePortletSelector(prop(type));
    const isAllChecked = items.every(item => item.preference);
    const { [`${type}All`]: showAll = false } = usePortletConfig();

    return {
        items,
        isAllChecked,
        showAll
    };
};

export const useFormatPlayerTags = (playerTags) => {
    const formatter = useFormatter();

    return useMemo(() => Object.entries(playerTags)
        .reduce((acc, [key, { name, type, value, expirationDate }]) => {
            const date = new Date();
            date.setDate(date.getDate() + Number(expirationDate));
            const expirationDateFormatted = formatter.date(date.toISOString(), '%Y-%m-%d');

            return { ...acc, [key]: { name, type, value, expirationDate: expirationDateFormatted } };
        }, {}), [formatter, playerTags]);
};

export const useParsePortletConfig = (config) => {
    const {
        promotional = [],
        account = [],
        thirdParty = [],
        playerTags = {},
        tagsEnabled = false,
        portletType: type,
        tkPrefix
    } = config;
    const formattedTags = useFormatPlayerTags(playerTags);

    return useMemo(() => ({
        type,
        promotional,
        account,
        thirdParty,
        tagsEnabled,
        tkPrefix,
        playerTags: formattedTags,
        promotionalAll: promotional.includes('all'),
        accountAll: account.includes('all')
    }), [type, promotional, account, thirdParty, tagsEnabled, tkPrefix, formattedTags]);
};
